"use client";

import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { NavItem } from "@/config/navigation";
import { cn } from "@/lib/utils";

export function MobileMenu({
  items,
  ctaHref,
  ctaLabel,
}: {
  items: NavItem[];
  ctaHref: string;
  ctaLabel: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  const openMenu = () => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    setOpen(true);
  };

  const closeMenu = () => {
    dialogRef.current?.close();
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleClose = () => {
      setOpen(false);
      triggerRef.current?.focus();
    };

    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openMenu}
        aria-label="Otevřít menu"
        aria-haspopup="dialog"
        className="inline-flex h-11 w-11 items-center justify-center rounded-xs text-ink md:hidden focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
      >
        <MenuIcon />
      </button>

      <dialog
        ref={dialogRef}
        aria-label="Hlavní navigace"
        className="m-0 h-[100dvh] max-h-none w-screen max-w-none bg-paper text-ink backdrop:bg-ink/40 md:hidden"
      >
        <div className="container-page flex min-h-[100dvh] flex-col py-6">
          <div className="flex items-center justify-between">
            <span className="font-display text-xl">Menu</span>
            <button
              type="button"
              onClick={closeMenu}
              aria-label="Zavřít menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
            >
              <X aria-hidden="true" size={20} strokeWidth={1.5} />
            </button>
          </div>

          <nav aria-label="Mobilní navigace" className="mt-10 flex-1">
            <ul className="flex flex-col">
              {items.map((item) => (
                <li key={item.href} className="border-b border-line">
                  <a
                    href={item.href}
                    onClick={closeMenu}
                    className="flex min-h-14 items-center font-display text-2xl text-ink"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <a
            href={ctaHref}
            onClick={closeMenu}
            className={cn(
              "mt-8 inline-flex min-h-13 w-full items-center justify-center rounded-xs bg-clay px-6",
              "font-sans text-sm font-medium text-paper hover:bg-clay-dark",
            )}
          >
            {ctaLabel}
          </a>
        </div>
      </dialog>
    </>
  );
}

function MenuIcon() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <line x1="3" y1="7" x2="21" y2="7" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="17" x2="21" y2="17" />
    </svg>
  );
}
